import { __rest } from "tslib";
import { jsx as _jsx } from "react/jsx-runtime";
import RadioButtonCheckedIcon from "@diligentcorp/atlas-react-icons/RadioButtonChecked.js";
import RadioButtonDefaultIcon from "@diligentcorp/atlas-react-icons/RadioButtonDefault.js";
import EllipsisHorizontalIcon from "@diligentcorp/atlas-react-icons/starling-hybrid/EllipsisHorizontal.js";
import StatusSuccessIcon from "@diligentcorp/atlas-react-icons/StatusSuccess.js";
import { Box, Step, StepLabel, Tooltip, Typography, useTheme } from "@mui/material";
import { useWindowSize as useWindowSizeDebounced } from "@react-hook/window-size";
import * as React from "react";
import { StepType, } from "../../../shared/presets/stepper.js";
import useChunkedSteps from "./useChunkedSteps.js";
const Icons = {
    [StepType.complete]: StatusSuccessIcon,
    [StepType.active]: RadioButtonCheckedIcon,
    [StepType.incomplete]: RadioButtonDefaultIcon,
};
function EllipsisWithTooltip({ content }) {
    const theme = useTheme();
    return (_jsx(Tooltip, Object.assign({ title: content, placement: "bottom", "aria-haspopup": "true", tabIndex: 0 }, { children: _jsx(Box, Object.assign({ sx: { paddingY: 1 } }, { children: _jsx(Box, { component: EllipsisHorizontalIcon, sx: {
                    color: theme.tokens.semantic.color.text.muted.value,
                    transform: "translateY(-4px)",
                } }) })) })));
}
function VisibleStep(_a) {
    var { children, id } = _a, moreProps = __rest(_a, ["children", "id"]);
    const [widthD] = useWindowSizeDebounced(); // default delay of 100ms
    return (_jsx(Step, Object.assign({}, moreProps, { children: children }), `${id}-${widthD}`));
}
function VisibleStepLabel(_a) {
    var { tooltipContent, type, text } = _a, moreProps = __rest(_a, ["tooltipContent", "type", "text"]);
    const [stepWidth, setStepWidth] = React.useState(null);
    const containerRef = React.useRef(null);
    React.useLayoutEffect(() => {
        var _a;
        if (containerRef && ((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth)) {
            setStepWidth(containerRef.current.offsetWidth);
        }
    }, [text, containerRef]);
    const IconComponent = Icons[type];
    const renderIconWrapper = () => (_jsx(Box, Object.assign({ sx: { paddingTop: 0.5, width: "100%" } }, { children: _jsx(IconComponent, {}) })));
    return (_jsx(StepLabel, Object.assign({ StepIconComponent: () => tooltipContent ? (_jsx(Tooltip, Object.assign({ title: tooltipContent, placement: "bottom", "aria-haspopup": "true", tabIndex: 0 }, { children: renderIconWrapper() }))) : (renderIconWrapper()) }, moreProps, { children: _jsx("div", Object.assign({ ref: containerRef }, { children: stepWidth ? (_jsx(Typography, Object.assign({ noWrap: true, maxWidth: `${stepWidth}px` }, { children: text }))) : null })) })));
}
function EllipsisStepLabel(_a) {
    var { tooltipContent, children } = _a, moreProps = __rest(_a, ["tooltipContent", "children"]);
    return (_jsx(StepLabel, Object.assign({ StepIconComponent: () => _jsx(EllipsisWithTooltip, { content: tooltipContent }) }, moreProps, { children: children })));
}
// Presets
export const stepperPresets = (tokens) => {
    return {
        advancedStepper: {
            activeStep: -1,
            alternativeLabel: true,
            sx: {
                ".MuiStepLabel-iconContainer": {
                    height: "44px",
                    position: "absolute",
                    textAlign: "center",
                    width: "100%",
                    "[aria-haspopup]": {
                        margin: "0 auto",
                        "&:focus-visible": {
                            borderRadius: tokens.semantic.radius.md.value,
                            boxShadow: tokens.semantic.shadow.focus.value.stringValue,
                            outline: "none",
                            zIndex: "1",
                        },
                    },
                    svg: {
                        color: tokens.semantic.color.text.muted.value,
                        fontSize: tokens.semantic.iconSize.lg.value,
                    },
                    "&.Mui-completed svg, &.Mui-active svg": {
                        color: tokens.semantic.color.action.default.value,
                    },
                },
                ".MuiStepLabel-label.MuiStepLabel-alternativeLabel": {
                    marginTop: "29px",
                    "&.Mui-active": {
                        ".MuiTypography-root": {
                            fontWeight: tokens.semantic.fontWeight.emphasis.value,
                        },
                    },
                },
                ".MuiTypography-root": {
                    fontSize: tokens.semantic.fontSize.text.md.value,
                    margin: "0 auto",
                },
                ".MuiStepConnector-root": {
                    marginTop: "3px",
                },
            },
        },
        constants: {
            StepType,
        },
        hooks: {
            useChunkedSteps,
        },
        components: {
            EllipsisStepLabel,
            VisibleStepLabel,
            VisibleStep,
        },
    };
};
//# sourceMappingURL=stepper.js.map